import React, { useContext, useState, useEffect } from 'react'
import { X } from 'lucide-react'
import ModalContext from '../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../contexts/OccurrenceContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import ApiService from '../../../services/ApiService'
import { Occurrence } from '../../../pages/ClientPanel/Occurrences/OccurencesTable/types'
import {
    Container,
    Header,
    TitleRow,
    CloseButton,
    InfosContainer,
    Section,
    SectionDivider,
    SectionSubtitle,
    InfoGrid,
    InfoItem,
    InfoLabel,
    InfoValue,
    TextBlock,
    TextBlockLabel,
    SimpleTable,
    SimpleTableRow,
    TableHeader,
    TableCell,
    RelevantBadge,
    ButtonContainer,
    ButtonDiv,
} from './style'

const ViewOccurrenceModal: React.FC = () => {
    const { setModalActive } = useContext(ModalContext)
    const { occurrence } = useContext(OccurrenceContext)
    const { setFeedback } = useContext(FeedbackContext)

    const [fullData, setFullData] = useState<Occurrence | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const loadData = async () => {
            if (!occurrence?.id) {
                setIsLoading(false)
                return
            }

            try {
                const response = await ApiService.occurrences.findById(occurrence.id)
                setFullData(response.data)
            } catch (error) {
                console.error('Erro ao carregar ocorrência:', error)
                setFullData(occurrence)
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [occurrence?.id])

    const data = fullData || occurrence

    const handleFileDownload = async (file: { id: number; name: string }) => {
        if (!data?.id) return
        try {
            const response = await ApiService.occurrences.downloadFile(data.id, file.id)
            const { url } = response.data
            const link = document.createElement('a')
            link.href = url
            link.download = file.name
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Erro ao baixar arquivo:', error)
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: 'Erro ao baixar o arquivo. Tente novamente.',
            })
        }
    }

    if (isLoading) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#8c98a4' }}>
                    Carregando dados da ocorrência...
                </div>
            </Container>
        )
    }

    if (!data) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#8c98a4' }}>
                    Nenhuma ocorrência selecionada.
                </div>
            </Container>
        )
    }

    const providences = data.providences || []

    const files = (data.FileInOccurrence || []).map(f => ({
        id: f.GeneralFile.id,
        name: f.GeneralFile.title,
    }))

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-'
        try {
            const date = new Date(dateStr)
            return date.toLocaleDateString('pt-BR')
        } catch {
            return dateStr
        }
    }

    return (
        <Container>
            <Header>
                <TitleRow>
                    <h2>Ocorrência #{data.id}</h2>
                    <RelevantBadge relevant={data.relevant}>
                        {data.relevant ? 'Relevante' : 'Não relevante'}
                    </RelevantBadge>
                </TitleRow>
                <CloseButton onClick={() => setModalActive(false)}>
                    <X size={16} />
                </CloseButton>
            </Header>

            <InfosContainer>
                {/* Informações Gerais */}
                <Section>
                    <SectionSubtitle>Informações gerais</SectionSubtitle>
                    <InfoGrid>
                        <InfoItem>
                            <InfoLabel>OP</InfoLabel>
                            <InfoValue>{data.operationNumber ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Cliente</InfoLabel>
                            <InfoValue>{data.companyName || data.contactName || 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Setor</InfoLabel>
                            <InfoValue>{data.sector ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Alocação</InfoLabel>
                            <InfoValue>{data.allocation ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Etapa</InfoLabel>
                            <InfoValue>{data.Stage?.name ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Quantidade</InfoLabel>
                            <InfoValue>{data.quantity ?? 'N/A'}</InfoValue>
                        </InfoItem>
                    </InfoGrid>
                </Section>

                <SectionDivider />

                {/* Descrição */}
                <Section>
                    <SectionSubtitle>Descrição</SectionSubtitle>
                    <TextBlock>{data.description || 'Sem descrição.'}</TextBlock>
                </Section>

                <SectionDivider />

                {/* Causa e Correção */}
                <Section>
                    <SectionSubtitle>Causa e Correção</SectionSubtitle>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <TextBlockLabel>Causa</TextBlockLabel>
                            <TextBlock>{data.cause || 'Não informada.'}</TextBlock>
                        </div>
                        <div>
                            <TextBlockLabel>Correção</TextBlockLabel>
                            <TextBlock>{data.correction || 'Não informada.'}</TextBlock>
                        </div>
                    </div>
                </Section>

                <SectionDivider />

                {/* Providências */}
                <Section>
                    <SectionSubtitle>Providências</SectionSubtitle>
                    {providences.length > 0 ? (
                        <SimpleTable>
                            <thead>
                                <tr>
                                    <TableHeader>Descrição</TableHeader>
                                    <TableHeader style={{ width: '180px' }}>Responsável</TableHeader>
                                    <TableHeader style={{ textAlign: 'center', width: '120px' }}>Prazo</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {providences.map((providence, index) => (
                                    <SimpleTableRow key={providence.id || index}>
                                        <TableCell>{providence.description || '-'}</TableCell>
                                        <TableCell>{providence.responsibleName || '-'}</TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            {formatDate(providence.deadline)}
                                        </TableCell>
                                    </SimpleTableRow>
                                ))}
                            </tbody>
                        </SimpleTable>
                    ) : (
                        <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
                            Nenhuma providência registrada.
                        </p>
                    )}
                </Section>

                {/* Anexos */}
                {files.length > 0 && (
                    <>
                        <SectionDivider />
                        <Section>
                            <SectionSubtitle>Anexos</SectionSubtitle>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {files.map(file => (
                                    <div
                                        key={file.id}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 14px',
                                            backgroundColor: '#FAFAFA',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            color: '#262626',
                                        }}
                                    >
                                        <span style={{ flex: 1, wordBreak: 'break-word', marginRight: '12px' }}>
                                            {file.name}
                                        </span>
                                        <span
                                            onClick={() => handleFileDownload(file)}
                                            style={{
                                                color: '#0090d3',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Download
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </InfosContainer>

            <ButtonContainer>
                <ButtonDiv
                    variant="ghost"
                    onClick={() => setModalActive(false)}
                    style={{ color: '#0090d3', fontWeight: 'bold' }}
                >
                    Fechar
                </ButtonDiv>
            </ButtonContainer>
        </Container>
    )
}

export default ViewOccurrenceModal
